/**
 * ProvenanceTrace overlay -- "How we got here" full-screen modal.
 *
 * Open on click of any rail metric, the inspector's state pill,
 * or the "Show how this was derived" button. Renders:
 *   * Title + value + formula + deriver
 *   * Lineage (inputs[] from the backend)
 *   * Sealed output (hash + prev_hash + as_of) -- the ChainSealOut
 *   * Verify chain action (admin-only at the API; non-admin gets
 *     a forbidden notice on click)
 *
 * Recursive drill-down: snapshot-kind inputs may carry a trace_ref
 * the operator can click to open a NESTED ProvenanceTrace. The
 * parent ConstellationView passes setTraceCoords to handle the
 * drill (replaces the current overlay's coords with the upstream's).
 */
import { useTranslation } from "react-i18next";

import { EscalationLine } from "shared/components/EscalationLine";

import { useProvenance, useVerifyChain } from "twin/api/constellation_hooks";
import type { ProvenanceInput, ProvenanceTrace as Trace } from "shared/api/types";

import { Arrow } from "shared/components/Arrow";
export interface TraceCoords {
  chain: string;
  entity_kind: string;
  entity_id: string;
  as_of?: string | null;
}

export interface ProvenanceTraceProps {
  coords: TraceCoords | null;
  onClose: () => void;
  onDrillInto: (next: TraceCoords) => void;
  isAdmin: boolean;
}

export function ProvenanceTrace({
  coords,
  onClose,
  onDrillInto,
  isAdmin,
}: ProvenanceTraceProps): JSX.Element | null {
  const { t } = useTranslation();
  const trace = useProvenance({
    chain: coords?.chain ?? null,
    entity_kind: coords?.entity_kind ?? null,
    entity_id: coords?.entity_id ?? null,
    as_of: coords?.as_of ?? null,
  });
  const verify = useVerifyChain();

  if (!coords) return null;

  return (
    <div
      data-testid="constellation-provenance-overlay"
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 p-4"
      onClick={onClose}
    >
      <div
        data-testid="constellation-provenance-modal"
        className="relative max-h-[90vh] w-full max-w-3xl overflow-y-auto rounded-lg bg-white p-5 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          data-testid="constellation-provenance-close"
          onClick={onClose}
          aria-label={t("modules.twin.constellation.provenance.close")}
          className="absolute end-3 top-3 text-slate-400 hover:text-slate-700"
        >
          ×
        </button>

        <p className="text-[10px] uppercase tracking-wide text-slate-500">
          {t("modules.twin.constellation.provenance.kicker")}
        </p>

        {trace.isPending && (
          <p
            data-testid="constellation-provenance-loading"
            className="mt-2 text-sm text-slate-500"
          >
            {t("modules.twin.constellation.provenance.loading")}
          </p>
        )}

        {trace.isError && (
          <div
            data-testid="constellation-provenance-error"
            className="mt-2 rounded border border-rose-200 bg-rose-50 p-3 text-sm text-rose-700"
          >
            <strong className="block font-semibold">
              {t("modules.twin.constellation.provenance.errorTitle")}
            </strong>
            {trace.error.detail}
          </div>
        )}

        {trace.data && (
          <Body
            trace={trace.data}
            onDrillInto={onDrillInto}
            isAdmin={isAdmin}
            verify={verify}
          />
        )}
      </div>
    </div>
  );
}

interface BodyProps {
  trace: Trace;
  onDrillInto: (next: TraceCoords) => void;
  isAdmin: boolean;
  verify: ReturnType<typeof useVerifyChain>;
}

function Body({ trace, onDrillInto, isAdmin, verify }: BodyProps): JSX.Element {
  const { t } = useTranslation();
  const runVerify = () => {
    if (!isAdmin) return;
    verify.mutate({ slug: trace.chain });
  };

  return (
    <>
      <h3
        data-testid="constellation-provenance-title"
        className="mt-1 text-xl font-semibold text-slate-900"
      >
        {trace.title}
      </h3>
      <p
        data-testid="constellation-provenance-value"
        className="mt-1 text-base text-slate-700"
      >
        {trace.value}
      </p>

      <section className="mt-4 rounded border border-slate-200 bg-slate-50 p-3">
        <p className="text-[10px] uppercase tracking-wide text-slate-500">
          {t("modules.twin.constellation.provenance.formula")}
        </p>
        <code
          data-testid="constellation-provenance-formula"
          className="block whitespace-pre-wrap break-words text-xs text-slate-800"
        >
          {trace.formula}
        </code>
        <span
          data-testid="constellation-provenance-deriver"
          className="mt-2 inline-block rounded-full border border-slate-300 bg-white px-2 py-0.5 text-[11px] text-slate-700"
        >
          {trace.deriver}
        </span>
      </section>

      <section className="mt-4" data-testid="constellation-provenance-lineage">
        <p className="text-[10px] uppercase tracking-wide text-slate-500">
          {t("modules.twin.constellation.provenance.lineage")}
        </p>
        {trace.inputs.length === 0 ? (
          <p
            data-testid="constellation-provenance-empty-inputs"
            className="mt-1 text-xs italic text-slate-500"
          >
            {t("modules.twin.constellation.provenance.emptyInputs")}
          </p>
        ) : (
          <ul className="mt-1 space-y-1">
            {trace.inputs.map((inp, i) => (
              <InputRow key={i} input={inp} onDrillInto={onDrillInto} />
            ))}
          </ul>
        )}

        <div className="mt-3 rounded border border-emerald-200 bg-emerald-50 p-2">
          <p className="text-[10px] uppercase tracking-wide text-emerald-700">
            {t("modules.twin.constellation.provenance.sealedOutput")}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
            <HashChip label="hash" value={trace.output.hash} />
            <span className="text-slate-500">
              <Arrow kind="forward" />
            </span>
            <HashChip label="prev_hash" value={trace.output.prev_hash} />
            {trace.output.captured_at && (
              <span className="text-slate-500">· {trace.output.captured_at}</span>
            )}
          </div>
        </div>
      </section>

      <div className="mt-4 flex items-center justify-between gap-2">
        <button
          type="button"
          data-testid="constellation-provenance-verify"
          onClick={runVerify}
          disabled={!isAdmin || verify.isPending}
          className="rounded bg-brand-brass px-3 py-1 text-sm font-medium text-brand-navy hover:bg-brand-brassDark disabled:cursor-not-allowed disabled:opacity-50"
        >
          {verify.isPending
            ? t("modules.twin.constellation.provenance.verifyRunning")
            : t("modules.twin.constellation.provenance.verifyAction")}
        </button>
        {!isAdmin && (
          <span
            data-testid="constellation-provenance-verify-forbidden"
            className="text-xs italic text-slate-500"
          >
            {t("modules.twin.constellation.provenance.verifyAdminOnly")}
          </span>
        )}
        {/* C4 -- the mutation's result outlived the chain it was about:
            opening a different node showed the PREVIOUS chain's verdict
            with no indication it belonged to something else. An
            integrity claim attached to the wrong subject is worse than
            no claim. React Query keeps the variables it was called
            with, so the result is rendered only against the chain it
            actually examined. */}
        {verify.isSuccess && verify.variables?.slug === trace.chain && (
          <span
            data-testid="constellation-provenance-verify-result"
            data-clean={verify.data.is_clean ? "true" : "false"}
            // A break rendered in green is a break nobody acts on: the
            // verdict has to carry in the colour as well as the words.
            className={
              "text-xs " + (verify.data.is_clean ? "text-emerald-700" : "text-rose-700")
            }
          >
            {verify.data.is_clean
              ? t("modules.twin.constellation.provenance.verifyOk", {
                  chain: trace.chain,
                  examined: verify.data.rows_examined,
                })
              : t("modules.twin.constellation.provenance.verifyBroken", {
                  at: (verify.data.breaks[0]?.offset ?? 0) + 1,
                  examined: verify.data.rows_examined,
                })}
          </span>
        )}
        {/* Round 16 C15 -- not tamper, but a governance fault the walk
            once could not see: one approval sealed twice. */}
        {verify.isSuccess &&
          verify.variables?.slug === trace.chain &&
          (verify.data.defects?.length ?? 0) > 0 && (
            <span
              data-testid="constellation-provenance-verify-defects"
              className="text-xs text-amber-800"
            >
              {t("modules.twin.constellation.provenance.verifyDefects", {
                count: verify.data.defects?.length ?? 0,
              })}
            </span>
          )}
        {verify.isError && verify.variables?.slug === trace.chain && (
          <span
            data-testid="constellation-provenance-verify-error"
            className="text-xs text-rose-700"
          >
            {verify.error.detail}
          </span>
        )}
      </div>
      {/* E5 -- a broken chain described itself and stopped. This is the
          one failure in the product with a named consequence: do not
          issue a dossier until it is resolved. */}
      {verify.isSuccess && !verify.data.is_clean && (
        <EscalationLine
          testid="provenance-escalate"
          who={t("escalate.chainBreak")}
          details={{
            what: `Hash chain "${trace.chain}" failed verification`,
            context: {
              Chain: trace.chain,
              "Rows examined": verify.data.rows_examined,
              "Rows valid": verify.data.rows_valid,
              Breaks: verify.data.breaks.length,
              "First break at row": (verify.data.breaks[0]?.offset ?? 0) + 1,
              "Break detail": verify.data.breaks[0]?.detail ?? "",
            },
          }}
        />
      )}
    </>
  );
}

interface InputRowProps {
  input: ProvenanceInput;
  onDrillInto: (next: TraceCoords) => void;
}

function InputRow({ input, onDrillInto }: InputRowProps): JSX.Element {
  const { t } = useTranslation();
  const drill = input.trace_ref;
  return (
    <li
      data-testid="constellation-provenance-input"
      data-kind={input.kind}
      className="rounded border border-slate-200 bg-white p-2"
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-slate-900">
            <span className="me-1 inline-block rounded-full border border-slate-300 bg-slate-50 px-1.5 py-0 text-[9px] uppercase tracking-wide text-slate-600">
              {input.kind}
            </span>
            {input.label}
          </p>
          <p className="text-[11px] text-slate-500">
            {input.source}
            {input.ref ? ` · ${input.ref}` : ""}
          </p>
          {input.detail && <p className="text-[11px] text-slate-600">{input.detail}</p>}
        </div>
        <HashChip label="hash" value={input.hash} />
      </div>
      {drill && (
        <button
          type="button"
          data-testid="constellation-provenance-drill"
          data-drill-chain={drill.chain}
          data-drill-entity-kind={drill.entity_kind}
          data-drill-entity-id={drill.entity_id}
          onClick={() => onDrillInto(drill)}
          className="mt-1 text-[11px] text-brand-brassDark hover:underline"
        >
          {t("screens.provenanceTrace.traceThisInput")}
        </button>
      )}
    </li>
  );
}

function HashChip({
  label,
  value,
}: {
  label: string;
  value: string | null;
}): JSX.Element {
  const { t } = useTranslation();
  return (
    <span
      title={`${label}: ${value ?? "—"}`}
      data-testid="constellation-provenance-hash-chip"
      className="rounded border border-slate-300 bg-white px-1.5 py-0 font-mono text-[10px] text-slate-700"
    >
      {value ? value.slice(0, 8) : t("screens.provenanceTrace.genesis")}
    </span>
  );
}
